import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FC, Fragment, useState } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import {
  Button,
  FieldRow,
  Flex,
  Modal,
  StyledFormGroup,
  StyledFormSelectFormik,
  WrappedQuery,
} from '../../../../../components/shared';
import { formatSelectValue } from '../../../../../utils/format';
import { Option, PeopleProfileOrganisationFormType } from '../../../../../utils/types';
import { ALL_ORGANISATIONS } from '../../../../../graphql';

const Actions = styled.div`
  display: flex;
`;

const defaultPeopleProfileOrganisationFormType: PeopleProfileOrganisationFormType = {
  id: '',
  designation: '',
  primary: false,
  organisation: undefined,
};

type EditOrganisationModalFormPropsType = {
  data: PeopleProfileOrganisationFormType;
  parentValues: any | null;
  onClose: (cancel: boolean, data: PeopleProfileOrganisationFormType) => void;
  open: boolean;
  viewMode?: boolean;
};

const EditOrganisationModalFormPropsTypeDefault: EditOrganisationModalFormPropsType = {
  data: defaultPeopleProfileOrganisationFormType,
  parentValues: null,
  onClose: (cancel: boolean, data: PeopleProfileOrganisationFormType) => {},
  open: false,
  viewMode: true,
};

type FormType = PeopleProfileOrganisationFormType & {
  // fields not for saving
  organisationId:
    | (Option & {
        id: string;
        name: string;
      })
    | undefined;
};

const EditOrganisationModalForm: FC<EditOrganisationModalFormPropsType> = props => {
  const viewMode = props.viewMode === true;
  const isNew = props.data.id === '';
  const [searchTerm, setSearchTerm] = useState({
    funder: '',
    institution: '',
    organisation: '',
    serviceProvider: '',
    tags: '',
  });

  const initialValues: FormType = {
    ...props.data,

    organisationId:
      !isNew && props.data.organisation
        ? {
            label: props.data.organisation.name,
            value: props.data.organisation.id,
            id: props.data.organisation.id,
            name: props.data.organisation.name,
          }
        : undefined,
  };

  const validationSchema = yup.object().shape({
    organisationId: yup
      .object()
      .label('Organisation')
      .required(),
  });

  return (
    <Fragment>
      <Modal
        open={props.open}
        onClose={() => props.onClose(true, props.data)}
        heading={isNew ? 'Link to Organisation' : 'Edit Organisation Association'}
        bodyOverflowVisible={true}
      >
        {props.open && (
          <Flex direction="column" size="1">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={values => {
                const result: PeopleProfileOrganisationFormType = { ...values };
                if (values.organisationId) {
                  result.organisation = {
                    id: values.organisationId.value,
                    name: values.organisationId.label,
                  };
                }
                delete result['organisationId'];
                props.onClose(false, result);
              }}
              render={(formikProps: FormikProps<FormType>) => {
                return (
                  <Fragment>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <WrappedQuery
                          query={ALL_ORGANISATIONS}
                          variables={{
                            input: {
                              first: 10,
                              searchTerm: searchTerm.organisation,
                            },
                          }}
                          overrideStates={true}
                        >
                          {({ data, error, loading, fetchMore }) => {
                            return (
                              <Field
                                name="organisationId"
                                render={({ field }: FieldProps<FormType>) => (
                                  <StyledFormSelectFormik
                                    {...field}
                                    field={field}
                                    label="Organisation"
                                    form={formikProps}
                                    loading={loading}
                                    placeholder={
                                      loading ? 'Data loading, Please wait' : 'Data ready, Please select option'
                                    }
                                    setSearchTerm={(data: string) =>
                                      data && setSearchTerm({ ...searchTerm, organisation: data })
                                    }
                                    options={
                                      loading || error
                                        ? []
                                        : data.organisations
                                        ? data.organisations.edges
                                            .map((edge: any) =>
                                              formatSelectValue({
                                                label:
                                                  (edge.node.funder && edge.node.funder.name) ||
                                                  (edge.node.institution && edge.node.institution.name) ||
                                                  (edge.node.serviceProvider && edge.node.serviceProvider.name),
                                                value: edge.node.id,
                                              })
                                            )
                                            .filter((option: any) => option.label && option.value)
                                        : []
                                    }
                                    scrollHandler={async () => {
                                      return (
                                        data.organisations &&
                                        data.organisations.pageInfo &&
                                        data.organisations.pageInfo.hasNextPage &&
                                        fetchMore({
                                          variables: {
                                            input: {
                                              first: 10,
                                              searchTerm: searchTerm.organisation,
                                              after:
                                                data.organisations &&
                                                data.organisations.pageInfo &&
                                                data.organisations.pageInfo.endCursor,
                                            },
                                          },
                                          updateQuery: (previousResult, { fetchMoreResult }) => {
                                            const newOrganisations = fetchMoreResult.organisations.edges;

                                            return {
                                              organisations: {
                                                aggregate: fetchMoreResult.organisations.aggregate,
                                                pageInfo: fetchMoreResult.organisations.pageInfo,
                                                edges: [...previousResult.organisations.edges, ...newOrganisations],
                                                __typename: fetchMoreResult.organisations.__typename,
                                              },
                                            };
                                          },
                                        })
                                      );
                                    }}
                                    mr={4}
                                  />
                                )}
                              />
                            );
                          }}
                        </WrappedQuery>
                      </FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          name="designation"
                          render={({ field }: FieldProps<FormType>) => (
                            <StyledFormGroup
                              {...field}
                              field={field}
                              form={formikProps}
                              label="Designation"
                              readOnly={viewMode}
                              mr={0}
                            />
                          )}
                        />
                      </FieldRow>
                    </FieldRow>
                    <FieldRow flex={0.5}>
                      <FieldRow flex={0.5}>
                        <Field
                          name="primary"
                          render={({ field }: FieldProps<FormType>) => (
                            <StyledFormGroup
                              {...field}
                              field={field}
                              form={formikProps}
                              label="Primary Organisation"
                              type="checkbox"
                              readOnly={viewMode}
                              mr={4}
                              defaultChecked={formikProps.values.primary}
                              onChange={(option: any) => {
                                formikProps.setFieldValue('primary', !formikProps.values.primary);
                              }}
                              wrapperWidth="40px"
                              width="40px"
                            />
                          )}
                        />
                      </FieldRow>
                    </FieldRow>
                    <FieldRow justifyContent={'flex-end'}>
                      <Flex direction="row" size="1" justify="flex-end">
                        <Actions>
                          {!viewMode ? (
                            <Fragment>
                              <Button
                                type="button"
                                text="Cancel"
                                mr={3}
                                onClick={() => props.onClose(true, props.data)}
                              />
                              <Button type="button" text="Save" onClick={() => formikProps.handleSubmit()} />
                            </Fragment>
                          ) : (
                            <Fragment>
                              <Button
                                type="button"
                                text="Close"
                                mr={0}
                                onClick={() => props.onClose(true, props.data)}
                              />
                            </Fragment>
                          )}
                        </Actions>
                      </Flex>
                    </FieldRow>
                  </Fragment>
                );
              }}
            />
          </Flex>
        )}
      </Modal>
    </Fragment>
  );
};

export const useEditOrganisationModalForm = (): [
  React.FC,
  (
    data: PeopleProfileOrganisationFormType,
    parentValues: any | null,
    onDone: (cancel: boolean, data: PeopleProfileOrganisationFormType) => void
  ) => void
] => {
  const [showModal, setShowModal] = useState<EditOrganisationModalFormPropsType>(
    EditOrganisationModalFormPropsTypeDefault
  );

  const show = (
    data: PeopleProfileOrganisationFormType,
    parentValues: any | null,
    onDone: (cancel: boolean, data: PeopleProfileOrganisationFormType) => void
  ) => {
    if (!showModal.open) {
      setShowModal({
        data: data,
        parentValues: parentValues,
        open: true,
        onClose: (cancel: boolean, data: PeopleProfileOrganisationFormType) => {
          onDone(cancel, data);
          setShowModal(EditOrganisationModalFormPropsTypeDefault);
        },
      });
    }
  };

  return [
    () => {
      return <Fragment>{showModal.open && <EditOrganisationModalForm {...showModal} />}</Fragment>;
    },
    show,
  ];
};
